/// <amd-module name="Wit/DeliveryPlans/PlanSettingsPanel/Components/MarkerSettingsView" />
import * as React from "react";
import { IObservableArray, IObservableValue, ObservableValue } from "VSS/Core/Observable";
import { TimerManagement } from "VSS/Core/TimerManagement";
import { newGuid } from "VSS/Core/Util/String";
import { Button } from "VSSUI/Button";
import { ColorDropdown } from "VSSUI/Color";
import { DatePicker } from "VSSUI/DatePicker";
import { FormItem } from "VSSUI/FormItem";
import { Observer } from "VSSUI/Observer";
import { ITableColumn, SimpleTableCell, Table, TableCell } from "VSSUI/Table";
import { TextField } from "VSSUI/TextField";
import { generateRandomColorHex, parseColor } from "VSSUI/Utilities/Color";
import { Marker } from "Wit/Clients/Work/Contracts/Work";
import { TableStatus } from "Wit/Common/WitSettingsViews/Components/TableStatus";
import { getDateInLocal, today } from "Wit/DeliveryPlans/Common/Utils/DateUtils";
import * as Resources from "Wit/DeliveryPlans/Resources";
import { SettingsViewConstants } from "../PlanSettingsConstants";
import { IMarkerSettingData } from "../PlanSettingsInterfaces";

export interface MarkerSettingsViewProps {
    /**
     * Indicates if the controls should be disabled
     */
    disabled?: boolean;

    /**
     * Indicates if the data is valid or not
     */
    isValid: IObservableValue<boolean>;

    /**
     * Existing marker settings
     */
    markers: IObservableArray<IMarkerSettingData>;

    /**
     * Set a dirtiness flag based on the current settings
     */
    setIsDirty: (settings: IMarkerSettingData[]) => void;
}

export function MarkerSettingsView(props: MarkerSettingsViewProps): JSX.Element {
    const { disabled, isValid, markers, setIsDirty } = props;

    const validationErrorMessage = new ObservableValue<string | undefined>(undefined);

    const setIsValid = () => {
        const valid = validationErrorMessage.value === undefined && areLabelsAndColorsValid(markers.value);

        if (isValid.value !== valid) {
            isValid.value = valid;
        }
    };

    const validate = () => {
        if (!validateUniqueMarkers(markers.value)) {
            validationErrorMessage.value = Resources.DuplicateMarkersErrorMessage;
        } else {
            validationErrorMessage.value = undefined;
        }

        setIsValid();
        setIsDirty(markers.value);
    };

    // debounce validation function by 500ms
    const timer = new TimerManagement();
    const debouncedValidate = timer.debounce(validate, 500);

    const onAddMarker = () => {
        markers.push({
            date: new ObservableValue(today()),
            id: newGuid(),
            label: new ObservableValue(""),
            color: new ObservableValue(generateRandomColorHex())
        });

        debouncedValidate();
    };

    const onRemoveMarker = (rowIndex: number) => {
        markers.splice(rowIndex, 1);

        debouncedValidate();
    };

    const renderDateColumn = (
        rowIndex: number,
        columnIndex: number,
        tableColumn: ITableColumn<IMarkerSettingData>,
        tableItem: IMarkerSettingData
    ): JSX.Element => {
        const onChange = (date: Date) => {
            tableItem.date.value = date;
            debouncedValidate();
        };

        return (
            <SimpleTableCell columnIndex={columnIndex} key={tableItem.id + "-col-" + columnIndex} tableColumn={tableColumn}>
                <div className="table-dropdown">
                    <DatePicker className="table-dropdown" disabled={disabled} onChange={onChange} value={tableItem.date} />
                </div>
            </SimpleTableCell>
        );
    };

    const renderLabelColumn = (
        rowIndex: number,
        columnIndex: number,
        tableColumn: ITableColumn<IMarkerSettingData>,
        tableItem: IMarkerSettingData
    ): JSX.Element => {
        const onChange = (event: any, value: string) => {
            tableItem.label.value = value;
            debouncedValidate();
        };

        return (
            <SimpleTableCell columnIndex={columnIndex} key={tableItem.id + "-col-" + columnIndex} tableColumn={tableColumn}>
                <Observer label={tableItem.label}>
                    {(props: { label: string }) => (
                        <FormItem error={!props.label} className="table-dropdown">
                            <TextField
                                disabled={disabled}
                                onChange={onChange}
                                placeholder={Resources.MakerLabelTextfieldPlaceholder}
                                value={tableItem.label}
                            />
                        </FormItem>
                    )}
                </Observer>
            </SimpleTableCell>
        );
    };

    const renderColorColumn = (
        rowIndex: number,
        columnIndex: number,
        tableColumn: ITableColumn<IMarkerSettingData>,
        tableItem: IMarkerSettingData
    ): JSX.Element => {
        const onChange = (value: string) => {
            tableItem.color.value = value;
            debouncedValidate();
        };

        return (
            <SimpleTableCell columnIndex={columnIndex} key={tableItem.id + "-col-" + columnIndex} tableColumn={tableColumn}>
                <Observer color={tableItem.color}>
                    {({ color }: { color: string }) => (
                        <ColorDropdown
                            ariaLabel={Resources.ColorColumnLabel}
                            className="marker-color-dropdown flex-grow"
                            color={color}
                            onColorSelected={onChange}
                        />
                    )}
                </Observer>
            </SimpleTableCell>
        );
    };

    const renderDeleteColumn = (
        rowIndex: number,
        columnIndex: number,
        tableColumn: ITableColumn<IMarkerSettingData>,
        tableItem: IMarkerSettingData
    ): JSX.Element => {
        return (
            <TableCell
                className="bolt-table-cell-side-action"
                columnIndex={columnIndex}
                key={tableItem.id + "-col-" + columnIndex}
                tableColumn={tableColumn}
            >
                <div className="flex-column flex-center">
                    <Button
                        className="bolt-table-cell-content-reveal padding-horizontal-4"
                        disabled={disabled}
                        iconProps={{ iconName: "Delete" }}
                        onClick={() => onRemoveMarker(rowIndex)}
                        subtle
                    />
                </div>
            </TableCell>
        );
    };

    const columns: ITableColumn<IMarkerSettingData>[] = [
        {
            id: "date",
            name: Resources.DateColumnLabel,
            renderCell: renderDateColumn,
            width: new ObservableValue(-50)
        },
        {
            id: "label",
            name: Resources.LabelColumnLabel,
            renderCell: renderLabelColumn,
            width: new ObservableValue(-50)
        },
        {
            id: "color",
            name: Resources.ColorColumnLabel,
            renderCell: renderColorColumn,
            width: new ObservableValue(175)
        },
        {
            id: "delete",
            renderCell: renderDeleteColumn,
            width: new ObservableValue(40)
        }
    ];

    return (
        <div className="rhythm-vertical-8">
            <div className="padding-horizontal-20 rhythm-vertical-8">
                <div>{Resources.PlanMarkersHeader}</div>
                <div className="secondary-text">{Resources.PlanMarkersDescription}</div>
            </div>
            <TableStatus validationErrorMessage={validationErrorMessage} />
            <Table ariaLabel={Resources.PlanMarkersHeader} className="delivery-plans-settings-table" columns={columns} itemProvider={markers} />
            {!disabled && (
                <div className="padding-horizontal-20 margin-top-8">
                    <Observer markers={markers}>
                        {(props: { markers: IMarkerSettingData[] }) => (
                            <Button
                                disabled={props.markers.length >= SettingsViewConstants.maxMarkers}
                                iconProps={{ iconName: "Add" }}
                                onClick={onAddMarker}
                                text={Resources.AddMarkerButtonLabel}
                            />
                        )}
                    </Observer>
                </div>
            )}
        </div>
    );
}

export function getMarkerSettingsAsMarkers(markers: IMarkerSettingData[]): Marker[] {
    return markers.map(marker => {
        return {
            color: marker.color.value,
            date: getDateInLocal(marker.date.value),
            label: marker.label.value
        };
    });
}

function areLabelsAndColorsValid(markers: IMarkerSettingData[]): boolean {
    return markers.findIndex(marker => !marker.label.value || !parseColor(marker.color.value)) < 0;
}

function validateUniqueMarkers(settings: IMarkerSettingData[]): boolean {
    const uniqueMarkers: { [fieldId: string]: string[] } = {};
    for (let i = 0, len = settings.length; i < len; i++) {
        const setting = settings[i];
        const date = setting.date.value.toString();
        const labelAndColor = setting.label.value.toLowerCase() + setting.color.value.toLowerCase();
        if (!uniqueMarkers[date]) {
            uniqueMarkers[date] = [];
        } else if (uniqueMarkers[date].indexOf(labelAndColor) >= 0) {
            // found a duplicate
            return false;
        }

        uniqueMarkers[date].push(labelAndColor);
    }

    return true;
}
